import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Clock, User, Package } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { Meeting } from '../../types';
import StatusBadge from '../ui/StatusBadge';
import clsx from 'clsx';

interface MeetingCardProps {
  meeting: Meeting;
  onClick?: () => void;
  className?: string;
}

export default function MeetingCard({ meeting, onClick, className }: MeetingCardProps) {
  const scheduledTime = new Date(meeting.startTime);

  return (
    <Link
      to={`/meeting/${meeting.id}`}
      onClick={onClick}
      className={clsx(
        'block bg-white rounded-lg shadow-sm border border-gray-200',
        'p-4 tap-target transition-all duration-150',
        'hover:shadow-md active:shadow-lg active:scale-[0.98]',
        className
      )}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-semibold text-gray-900 truncate">
            {meeting.contact?.name || 'Cliente'}
          </h3>
          <p className="text-sm text-gray-500 mt-0.5">
            {meeting.contact?.phone || '—'}
          </p>
        </div>
        <StatusBadge status={meeting.status} />
      </div>

      {/* Details */}
      <div className="space-y-2">
        {/* Time */}
        <div className="flex items-center text-sm text-gray-600">
          <Clock className="w-4 h-4 mr-2 text-gray-400 flex-shrink-0" />
          <span>
            {format(scheduledTime, "HH:mm", { locale: ptBR })} - {meeting.duration} min
          </span>
        </div>

        {/* Product/Service */}
        {meeting.product && (
          <div className="flex items-center text-sm text-gray-600">
            <Package className="w-4 h-4 mr-2 text-gray-400 flex-shrink-0" />
            <span className="truncate">{meeting.product.name}</span>
          </div>
        )}

        {/* Operator (if different from current user) */}
        {meeting.user && (
          <div className="flex items-center text-sm text-gray-600">
            <User className="w-4 h-4 mr-2 text-gray-400 flex-shrink-0" />
            <span className="truncate">{meeting.user.name}</span>
          </div>
        )}

        {/* Notes preview */}
        {meeting.notes && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            <p className="text-sm text-gray-600 line-clamp-2">{meeting.notes}</p>
          </div>
        )}
      </div>
    </Link>
  );
}
